<template>
  <div>
    <div class="upload-btn-wrapper">
      <div class="file-upload-content">
        <img :src="imageSrc" class="file-upload-image" :class="{'p-2': optimizing || filesNb > 0}" alt="Upload" />
        <div class="file-upload-text">{{uploadText}}</div>
        <div :class="{hideVisibility: optimizing}">
          <som-button size="sm" @click.prevent.stop="$refs.fileInput.click()">{{uploadSubText}}</som-button>
        </div>
      </div>
      <div v-if="dragging || optimizing" class="dragging-over">
        <div v-if="dragging" class="dragging-over-inner" />
      </div>
      <input
        :id="inputId"
        ref="fileInput"
        type="file"
        name="file"
        accept="image/*"
        @click="onClick"
        @change="onFileUpload"

        @drag.prevent.stop
        @dragstart.prevent.stop
        @dragend.prevent.stop
        @dragover.prevent.stop="onDragOver"
        @dragenter.prevent.stop="onDragOver"
        @dragleave.prevent.stop="onDragLeave"
        @drop.prevent.stop="onDrop"
        :tabindex="-1"
      />
    </div>
  </div>
</template>

<script>
const IMAGE_MAX_SIZE = 2000; // Max height and width will be set to 2000 pixels

// toBlob polyfill (https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob#Polyfill)
if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
    value: function (callback, type, quality) {
      var dataURL = this.toDataURL(type, quality).split(',')[1];
      setTimeout(function() {
        var binStr = atob( dataURL ),
            len = binStr.length,
            arr = new Uint8Array(len);
        for (var i = 0; i < len; i++ ) {
          arr[i] = binStr.charCodeAt(i);
        }
        callback( new Blob( [arr], {type: type || 'image/png'} ) );
      });
    }
  });
}

export default {
  name: 'FileUploader',
  props: {
    inputId: String,
  },
  data() {
    return {
      files: [],
      dataUrls: [],
      dragging: false,
      optimizing: false,
    }
  },
  computed: {
    filesNb() {
      return this.files.length;
    },
    uploadText() {
      if (this.optimizing) {
        return "Optimizing Photo...";
      }
      return this.filesNb > 0 ? `${this.filesNb} Photo${this.filesNb > 1 ? 's' : ''} Ready to Upload` : `Drag & Drop a Photo Here`;
    },
    uploadSubText() {
      return this.filesNb > 0 ? `Change Photo` : `Select a Photo`;
    },
    imageSrc() {
      if (this.optimizing) {
        return 'images/15-web-programming-design-construction-3.png';
      }
      return this.filesNb > 0 ? 'images/icon-success.png' : 'images/07-cloud-upload.png';
    },
    fileUrls() {
      return this.files.map(file => window.URL.createObjectURL(file));
    },
  },
  methods: {
    getImage() {
      let dataUrl = '';
      let ext = '';
  
      if (this.dataUrls.length > 0) {
        dataUrl = this.dataUrls[0];

        // Extract file type from data URL
        const regex = new RegExp(/^data:image\/(.*);base64,/);
        const result = dataUrl.match(regex);
        if (result.length > 1) {
          ext = result[1];
        }
      }

      return {
        data: dataUrl,
        ext
      }
    },

    getFiles() {
      return this.files;
    },

    getImageDataUrls() {
      return this.dataUrls;
    },

    async verifyAndCompressFiles(files) {
      // Grab images
      const filesArray = [...files];
      const compressPromises = filesArray.map(async file => {
        let fileToReturn = file;
        if (/^image*/.test(file.type)) {
          const compressedImage = await this.compressImage(file);
          if (compressedImage.size < file.size) {
            // Only return the compressed image if compression worked (smaller image)
            fileToReturn = compressedImage;
          }
        }

        return new Promise((resolve) => resolve(fileToReturn));
      })

      // Return compressed files
      return await Promise.all(compressPromises);
    },

    async onFileUpload(/* event */) {
      this.prepareFiles(this.$refs.fileInput.files);
    },

    onClick() {
      // Reset the files when clicking on file input
      this.files = [];
    },

    onDragOver(/* event */) {
      this.dragging = true;
    },

    onDragLeave(/* event */) {
      this.dragging = false;
    },

    async prepareFiles(files) {
      this.optimizing = true;

      this.files = await this.verifyAndCompressFiles(files);

      let promises = [];
      for (let i = 0; i < this.files.length; i++) {
        promises.push(this.readFile(this.files[i]));
      }

      this.dataUrls = await Promise.all(promises);
      this.optimizing = false;
    },

    onDrop(event) {
      this.dragging = false;
      const files = event.dataTransfer.files;
      this.prepareFiles(files);
    },

    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = event => {
          resolve(event.target.result);
        };
        reader.onerror = error => reject(error);
      });
    },

    // https://zocada.com/compress-resize-images-javascript-browser
    // https://gist.github.com/chaman-k/a64fcd49c9e1c0b6a41074fdecf0e5d0#file-compress-js
    compressImage(imageFile) {
      return new Promise(async (resolve, reject) => {
        // Read uploaded image
        try {
          const base64Image = await this.readFile(imageFile);

          const img = new Image();
          img.src = base64Image;

          img.onload = () => {
            const elem = document.createElement('canvas');
            if (img.width > img.height) {
              // Restrict width and adjust height using scale factor
              const width = Math.min(IMAGE_MAX_SIZE, img.width);
              const scaleFactor = width / img.width;
              
              elem.width = width;
              elem.height = img.height * scaleFactor;
            } else {
              // Restrict height and adjust width using scale factor
              const height = Math.min(IMAGE_MAX_SIZE, img.height);
              const scaleFactor = height / img.height;
              elem.width = img.width * scaleFactor;
              elem.height = height;
            }

            const ctx = elem.getContext('2d');
            ctx.drawImage(img, 0, 0, elem.width, elem.height);
            
            ctx.canvas.toBlob((blob) => {
              resolve(blob);
            }, 'image/jpeg', 0.92);

            /*
            const data = ctx.canvas.toDataURL(img, 'image/jpeg', 1);
            return resolve({
              name: fileName,
              type: 'image/jpeg',
              data,
            });
            */
          }
        } catch (e) {
          reject(e);
        }
      });
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .upload-btn-wrapper {
    position: relative;
    text-align: center;
    border: 1px solid #8ea6af;
    border-radius: .25rem;
    padding: 2rem;
    overflow: hidden;
  }

  .upload-btn-wrapper input[type=file] {
    padding: 0;
    border: none;
    font-size: 0;
    margin: 0;
    position: absolute;
    left: 0;
    top: -20px;
    bottom: 0;
    width: 100%;
    height: calc(100% + 20px); /* Add offset added by 'top: -20px' */
    cursor: pointer !important;
    z-index: 1000;
    opacity: 0;
  }


  .upload-btn-wrapper label {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    cursor: pointer !important;
    z-index: 1000;
  }

  .file-upload-content {
    position: relative;
    cursor: pointer;
  }

  .file-upload-image {
    height: 5rem;
    width: auto;
  }

  .file-upload-text {
    font-weight: bold;
    color: rgb(52, 118, 163);
    margin-bottom: 1rem;
  }

  .dragging-over {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: black;
    opacity: 0.2;
    flex: 1;
    padding: 0.5rem;
    display: flex;
  }

  .dragging-over-inner {
    display: flex;
    flex: 1;

    border: 2px dashed rgba(255, 255, 255, 0.7);
    border-radius: .25rem;
  }
</style>
